import { useCallback, useEffect, useState } from "react";
import { api, errMsg, newIdemKey, type ClosingStatus } from "../api";
import { fmtDay, formatMoney } from "../lib/money";
import { useMe } from "../session";
import { Modal } from "./Modal";
import { Btn, Field, Input, Notice } from "./ui";

const monthName = (m: string) => new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(`${m}-01T12:00:00Z`));

/** Fechar posição do mês da conta: habilita só com tudo conciliado; fechado = congelado. */
export function PositionClosing({ accountId, month, currency, canWrite, version, onStatus, onChanged }: {
  accountId: string; month: string; currency: "BRL" | "USD"; canWrite: boolean; version: number; onStatus: (s: ClosingStatus | null) => void; onChanged: () => void;
}) {
  const { me } = useMe();
  const realm = me.user.realm;
  const [st, setSt] = useState<ClosingStatus | null>(null);
  const [dlg, setDlg] = useState<null | "close" | "reopen">(null);
  const [err, setErr] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const s = await api<ClosingStatus>("GET", `/api/${realm}/accounts/${accountId}/closing?month=${month}`); setSt(s); onStatus(s); }
    catch { setSt(null); onStatus(null); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realm, accountId, month]);
  useEffect(() => { void load(); }, [load, version]);

  async function act(kind: "close" | "reopen") {
    setBusy(true); setErr(null);
    try {
      if (kind === "close") await api("POST", `/api/${realm}/accounts/${accountId}/closing`, { month }, newIdemKey());
      else await api("POST", `/api/${realm}/accounts/${accountId}/closing/reopen`, { month, reason: reason.trim() }, newIdemKey());
      setDlg(null); setReason(""); await load(); onChanged();
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  }
  if (!st || st.blockers.beforeAccountStart) return null;
  const b = st.blockers;
  const why: string[] = [];
  if (b.monthNotEnded) why.push("o mês ainda não terminou");
  if (b.previousOpen) why.push(`feche antes ${b.previousOpen.slice(5)}/${b.previousOpen.slice(0, 4)}`);
  if (b.planned) why.push(`${b.planned} planejado${b.planned > 1 ? "s" : ""} sem baixa`);
  if (b.unreconciled) why.push(`${b.unreconciled} sem conciliação`);

  return (
    <div className="card mv-summary" data-position-closing>
      <h3>Posição do mês</h3>
      {st.closed ? (
        <>
          <p style={{ margin: "4px 0" }} data-closed-label>🔒 <b>Posição fechada</b> em {fmtDay((st.closedAt ?? "").slice(0, 10))}</p>
          <p className="small muted" style={{ margin: "4px 0" }}>Saldo final em {monthName(month)}: <b>{formatMoney(st.closingBalance!.decimal, currency)}</b>. Os lançamentos até o fim deste mês não aceitam alterações.</p>
          {st.canReopen && <Btn variant="ghost" onClick={() => { setErr(null); setDlg("reopen"); }} data-reopen-position>Reabrir posição</Btn>}
        </>
      ) : (
        <>
          {canWrite && <Btn onClick={() => { setErr(null); setDlg("close"); }} disabled={!st.canClose} data-close-position title={st.canClose ? "" : `Para fechar: ${why.join("; ")}`}>Fechar posição</Btn>}
          {!st.canClose && why.length > 0 && <p className="small muted" style={{ margin: "6px 0 0" }} data-close-why>Para fechar: {why.join("; ")}.</p>}
          {st.canClose && <p className="small muted" style={{ margin: "6px 0 0" }}>Tudo conciliado. Ao fechar, o mês fica protegido contra edição.</p>}
        </>
      )}
      {dlg === "close" && (
        <Modal title={`Fechar posição — ${monthName(month)}`} onClose={() => setDlg(null)}>
          <div data-close-dialog>
            {err && <Notice kind="error">{err}</Notice>}
            <p className="small">Todos os lançamentos do mês estão conciliados. Ao fechar, <b>nada</b> desta conta com data até o fim do mês poderá ser criado, editado, cancelado nem ter a conciliação desfeita. Só o titular pode reabrir, informando o motivo.</p>
            <div className="modal-actions"><Btn variant="ghost" onClick={() => setDlg(null)}>Voltar</Btn><button type="button" className="btn primary" disabled={busy} onClick={() => void act("close")} data-close-confirm>{busy ? "Aguarde…" : "Confirmar fechamento"}</button></div>
          </div>
        </Modal>
      )}
      {dlg === "reopen" && (
        <Modal title={`Reabrir posição — ${monthName(month)}`} onClose={() => setDlg(null)}>
          <form onSubmit={(e) => { e.preventDefault(); if (reason.trim().length >= 10) void act("reopen"); }}>
            {err && <Notice kind="error">{err}</Notice>}
            <Field label="Motivo da reabertura (mínimo 10 letras)" required><Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} name="reopenReason" /></Field>
            <div className="modal-actions"><Btn variant="ghost" onClick={() => setDlg(null)}>Voltar</Btn><button type="submit" className="btn danger" disabled={busy || reason.trim().length < 10} data-reopen-confirm>Reabrir</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
